<template>
  <m-pageframe title="{organization-label}: Edit Guest Check In Form">
    <v-container fluid style="padding-left: 30px; padding-right: 30px;">
      <widget-editor-layout
        v-if="form"
        ref="widgetLayout"
        :settings_collapsed="left_pane_collapsed"
        :active_tab.sync="active_tab"
        :back_route="back_route"
        back_label="All Guest Check-In Forms"
        :is_published="is_published"
        :is_archived="form.is_archived"
        :can_unarchive="can_unarchive"
        :item_label="form.internal_label"
        archive_dialog_title="Archive Guest Check-In Form"
        :has_draft_changes="has_draft_changes"
        :publish_time="form.publish_time"
        :publish_signature_id="form.publish_signature_id"
        :change_descriptions="edit_status?.change_descriptions || []"
        :validation_warnings="validation_warnings"
        publish_dialog_title="Publish Guest Check-In Form"
        @copy-url="copyPublicUrl"
        @generate-qr="generateQRCode"
        @archive="archiveForm"
        @unarchive="unarchiveForm"
        @publish="handlePublish"
        @discard-changes="handleDiscardChanges"
        @fix-warning="fixWarning"
      >
        <template
          #publish-agreement="{ publish_agreement_accepted, update_publish_agreement_accepted }"
        >
          <m-legal-checkbox-agreement
            type="guest-check-in-form-publication-agreement"
            :organization_id="$route.params.organization_id"
            :organization_label="form ? form.organization_label : ''"
            :value="publish_agreement_accepted"
            @input="update_publish_agreement_accepted"
            ref="publish-agreement"
            class="mb-4"
          />
        </template>

        <template #general-settings>
          <v-expansion-panels class="section-group mb-6" v-model="expanded_general" multiple>
            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-form-select"
                title="Form Settings"
                subtitle="Configure the form's name, status, and reset timer"
              />
              <v-expansion-panel-content class="py-4">
                <v-row class="my-1">
                  <v-col cols="12">
                    <v-text-field
                      ref="formNameInput"
                      data-validation-target="formNameInput"
                      v-model="form.internal_label"
                      v-if="form"
                      label="Form Name"
                      outlined
                      hide-details
                      dense
                      :rules="[rules.required]"
                      @input="onChange"
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      :value="public_url"
                      label="Published Form URL"
                      outlined
                      dense
                      readonly
                      :hint="publicUrlHint"
                      persistent-hint
                      :disabled="!form || !form.guest_check_in_form_id || !is_published"
                    >
                      <template v-slot:append>
                        <v-btn
                          icon
                          small
                          color="primary"
                          @click="copyPublicUrl"
                          :disabled="!form || !form.guest_check_in_form_id || !is_published"
                        >
                          <v-icon>mdi-content-copy</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" md="8">
                    <v-text-field
                      v-model.number="form.reset_timer_seconds"
                      label="Reset Timer (seconds)"
                      type="number"
                      outlined
                      dense
                      hint="Time before form resets after submission (default: 10 seconds)"
                      persistent-hint
                      @input="onChange"
                    />
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-message-text-outline"
                title="Messaging Settings"
                subtitle="Configure Double Opt-In and messages sent to guests"
              />
              <v-expansion-panel-content class="py-4">
                <div class="d-flex align-center">
                  <m-edit-select-enum
                    ref="doiStateSelect"
                    data-validation-target="doiStateSelect"
                    label="Double Opt In"
                    v-model="form.double_opt_in_state"
                    enum_name="form_double_opt_in_state"
                    dialog_title="Select Double Opt In State"
                    class="flex-grow-1"
                  />
                  <m-double-opt-in-explanation
                    class="ml-2"
                    :organization_id="$route.params.organization_id"
                  >
                    <template #activator="{ on, attrs }">
                      <div v-bind="attrs" v-on="on">
                        <v-tooltip top>
                          <template v-slot:activator="{ on: tipOn, attrs: tipAttrs }">
                            <v-btn icon small v-bind="tipAttrs" v-on="tipOn">
                              <v-icon color="primary">mdi-information-outline</v-icon>
                            </v-btn>
                          </template>
                          <span>Click to open double opt-in guide</span>
                        </v-tooltip>
                      </div>
                    </template>
                  </m-double-opt-in-explanation>
                </div>
                <v-switch
                  v-if="form.double_opt_in_state === 'disabled'"
                  v-model="form.no_double_opt_in_after_submission_message_enabled"
                  label="Send After Submission Message"
                  @change="onChange"
                  class="mb-4"
                />
                <div class="mt-4">
                  <m-edit-select-organization-owned-endpoint
                    ref="sendFromEndpointSelect"
                    data-validation-target="sendFromEndpointSelect"
                    :organization_id="$route.params.organization_id"
                    v-model="form.send_from_endpoint"
                    label="Send messages from"
                    :include_bot_numbers="true"
                    @input="onChange"
                    @validation="handleEndpointValidation"
                    class="mb-2"
                  />
                </div>
                <div>
                  <edit-message-patterns-compact
                    v-if="form.double_opt_in_state === 'enabled'"
                    label="Double opt-in message"
                    v-model="form.double_opt_in_message_patterns"
                    context="form-after-submit"
                    :default_value="get_default_double_opt_in_message_patterns()"
                    @input="onChange"
                    class="mb-4"
                    ref="doiMessageInput"
                    data-validation-target="doiMessageInput"
                  />
                  <edit-message-patterns-compact
                    v-if="form.double_opt_in_state === 'enabled'"
                    label="After double opt-in message"
                    v-model="form.after_double_opt_in_message_patterns"
                    context="form-after-submit"
                    :default_value="get_default_after_double_opt_in_message_patterns()"
                    @input="onChange"
                    class="mb-4"
                    ref="afterDOIInput"
                    data-validation-target="afterDOIInput"
                  />
                  <edit-message-patterns-compact
                    v-if="form.double_opt_in_state === 'disabled'"
                    label="After submission message"
                    v-model="form.no_double_opt_in_after_submission_message_patterns"
                    context="form-after-submit"
                    :default_value="
                      get_default_no_double_opt_in_after_submission_message_patterns()
                    "
                    @input="onChange"
                    class="mb-4"
                    ref="afterSubmitInput"
                    data-validation-target="afterSubmitInput"
                  />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-connection"
                title="Integrations"
                subtitle="Connect with RedChirp lists and external CRM systems"
              />
              <v-expansion-panel-content class="py-4">
                <div class="subtitle-1 font-weight-medium mb-3">CRM Integration Settings</div>
                <div class="px-md-4">
                  <m-compatible-integrations-hint
                    feature-identifier="customers-auto-enrich-from-form-submission"
                    before-link-text="Customer auto-enrichment from guest check-in submissions requires a"
                    link-text="compatible CRM integration"
                    after-link-text="to be enabled for your organization."
                    modal-title="Compatible CRM Integrations"
                    modal-description="Customer enrichment from guest check-in submissions is currently supported for the following CRM integrations:"
                    no-integrations-message="No compatible CRM integrations are currently enabled for your organization."
                    footer-message="More integrations will be added in the future. If your CRM is not listed above, the guest check-in form will still function but customer data will not be automatically synced to your CRM."
                    :show-hint="!customer_auto_enrich_feature_available"
                  />
                  <m-edit-select-enum
                    label="On Form Submission"
                    enum_name="crm_customer_upsert_mode"
                    v-model="form.on_submit_crm_customer_upsert_mode"
                    @input="onChange"
                    :read_only="!customer_auto_enrich_feature_available"
                    hint="Action to take in your CRM when a customer submits the form."
                    class="mb-4"
                  />
                  <m-edit-select-enum
                    v-if="form.double_opt_in_state === 'enabled'"
                    label="After Double Opt-In"
                    enum_name="crm_customer_upsert_mode"
                    v-model="form.after_double_opt_in_crm_customer_upsert_mode"
                    @input="onChange"
                    :read_only="!customer_auto_enrich_feature_available"
                    hint="Action to take in your CRM after a customer confirms their subscription."
                    class="mb-4"
                  />
                  <v-select
                    v-if="show_tag_selection"
                    label="Tag for New Customers"
                    v-model="form.new_customer_tag_id"
                    :items="integration_tags_with_none"
                    item-text="tag_label"
                    item-value="tag_id"
                    clearable
                    :disabled="!customer_auto_enrich_feature_available"
                    hint="Select a manual tag to automatically apply to newly created customers from guest check-in submissions. This tag will NOT be applied to existing customers. Select 'No Tag' to disable tagging."
                    persistent-hint
                    class="mt-3"
                    @change="onNewCustomerTagChange"
                  >
                    <template v-slot:no-data>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>No tags available</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-select>
                </div>
                <div class="subtitle-1 font-weight-medium mb-3 mt-2">
                  RedChirp Integration Settings
                </div>
                <div class="px-md-4">
                  <v-select
                    :label="
                      form.double_opt_in_state === 'enabled'
                        ? 'After double opt-in, add contact to list(s)'
                        : 'On submission, add contact to list(s)'
                    "
                    v-model="form.add_to_lists"
                    :items="available_lists"
                    item-text="text"
                    item-value="value"
                    @change="onChange"
                    multiple
                    chips
                    clearable
                    persistent-hint
                    hint="At minimum, you must have a phone number in the form submission to add the customer to a list. Only customers who provide a phone number with consent will be added to lists."
                  >
                    <template v-slot:no-data>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>No lists available</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-select>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-text"
                title="Agreement"
                subtitle="Manage initial consent gathering and agreement text"
              />
              <v-expansion-panel-content>
                <div class="d-flex align-center mb-3 mt-4">
                  <div class="subtitle-1 font-weight-medium">Consent Gathering</div>
                </div>
                <div class="mt-4">
                  <v-card outlined class="mb-4" data-validation-target="emailAgreementSettings">
                    <v-card-title class="text-h6 pb-2" >
                      <v-icon left>mdi-email</v-icon>
                      Email Agreement
                    </v-card-title>
                    <v-card-text>
                      <m-edit-select-enum
                        label="Agreement Type"
                        v-model="form.email_consent_agreement_type"
                        enum_name="consent_agreement_type"
                        dialog_title="Select Agreement Type"
                        class="mb-4"
                        hide_details
                        @change="onChange"
                      />
                      <div v-if="form.email_consent_agreement_type === 'custom'" class="mb-4">
                        <edit-message-patterns-compact
                          label="Custom Agreement Patterns"
                          v-model="form.email_consent_agreement_custom_patterns"
                          hint="Custom patterns for email agreement text. Use variables like {organization-label}, {terms-link}, {privacy-policy-link}"
                          :default_value="
                            getDefaultEmailAgreementPattern()
                              ? [getDefaultEmailAgreementPattern()]
                              : null
                          "
                          @input="onChange"
                        />
                      </div>
                      <div v-else class="mb-6">
                        <v-alert type="info" dense text class="mt-2">
                          Using default agreement pattern. The resolved text is shown in the
                          preview.
                        </v-alert>
                      </div>
                      <m-edit-select-enum
                        label="Consent Gathering Mode"
                        v-model="form.email_consent_gathering_state"
                        enum_name="form_consent_gathering_state"
                        dialog_title="Select Consent Gathering Mode"
                        @change="onEmailConsentGatheringChange"
                        :read_only="form.email_address.field_mode == 'hidden'"
                        :filter_options="filteredConsentGatheringOptions"
                      />
                      <v-alert
                        v-if="form.email_address.field_mode === 'hidden'"
                        type="info"
                        class="mb-6"
                        dense
                        text
                      >
                        Consent gathering is set to "No Consent Agreement Required" because the email field is
                        hidden.
                      </v-alert>

                      <div data-validation-target="emailFieldRequirement" class="mt-2 mb-6">
                        <v-select
                          :items="email_field_mode_options"
                          v-model="form.email_address.field_mode"
                          label="Field requirement"
                          hide-details
                          dense
                          outlined
                          @change="onEmailFieldModeChange"
                        />
                      </div>

                      <m-edit-css
                        label="Email Agreement Styling"
                        v-model="form.email_agreement_style.style"
                        :default_value="form.email_agreement_style.default_style"
                        :mobile_value.sync="form.email_agreement_style.mobile_styles"
                        :default_mobile_value="form.email_agreement_style.default_mobile_styles"
                        class="mb-4"
                      />
                    </v-card-text>
                  </v-card>
                  <v-card outlined class="mb-4" data-validation-target="phoneAgreementSettings">
                    <v-card-title class="text-h6 pb-2">
                      <v-icon left>mdi-phone</v-icon>
                      Phone Number Agreement
                    </v-card-title>
                    <v-card-text>
                      <m-edit-select-enum
                        label="Agreement Type"
                        v-model="form.phone_number_consent_agreement_type"
                        enum_name="consent_agreement_type"
                        dialog_title="Select Agreement Type"
                        class="mb-4"
                        hide_details
                        @change="onChange"
                      />
                      <div
                        v-if="form.phone_number_consent_agreement_type === 'custom'"
                        class="mb-4"
                      >
                        <edit-message-patterns-compact
                          label="Custom Agreement Patterns"
                          v-model="form.phone_number_consent_agreement_custom_patterns"
                          hint="Custom patterns for phone agreement text. Use variables like {organization-label}, {terms-link}, {privacy-policy-link}"
                          :default_value="
                            getDefaultPhoneAgreementPattern()
                              ? [getDefaultPhoneAgreementPattern()]
                              : null
                          "
                          @input="onChange"
                        />
                      </div>
                      <div v-else class="mb-6">
                        <v-alert type="info" dense text class="mt-0">
                          Using default agreement pattern. The resolved text is shown in the
                          preview.
                        </v-alert>
                      </div>
                      <m-edit-select-enum
                        ref="phoneConsentModeSelect"
                        data-validation-target="phoneConsentModeSelect"
                        label="Consent Gathering Mode"
                        v-model="form.phone_number_consent_gathering_state"
                        enum_name="form_consent_gathering_state"
                        dialog_title="Select Consent Gathering Mode"
                        @change="onPhoneConsentGatheringChange"
                        :read_only="form.phone_number.field_mode == 'hidden'"
                        :filter_options="filteredConsentGatheringOptions"
                      />
                      <v-alert
                        v-if="form.phone_number.field_mode === 'hidden'"
                        type="info"
                        class="mb-6"
                        dense
                        text
                      >
                        Consent gathering is set to "No Consent Agreement Required" because the phone field is hidden.
                      </v-alert>

                      <div data-validation-target="phoneFieldRequirement" class="mt-2 mb-6">
                        <v-select
                          :items="phone_field_mode_options"
                          v-model="form.phone_number.field_mode"
                          label="Field requirement"
                          hide-details
                          dense
                          outlined
                          @change="onPhoneFieldModeChange"
                        />
                      </div>

                      <m-edit-css
                        label="Phone Number Agreement Styling"
                        v-model="form.phone_number_agreement_style.style"
                        :default_value="form.phone_number_agreement_style.default_style"
                        :mobile_value.sync="form.phone_number_agreement_style.mobile_styles"
                        :default_mobile_value="
                          form.phone_number_agreement_style.default_mobile_styles
                        "
                        class="mb-4"
                      />
                    </v-card-text>
                  </v-card>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-account-check"
                title="Age Gate Settings"
                subtitle="Require users to confirm their age before submission"
              />
              <v-expansion-panel-content class="pt-6">
                <v-text-field
                  ref="ageGateInput"
                  data-validation-target="ageGateInput"
                  v-model.number="form.age_gate_min_age_in_years"
                  label="Minimum Age"
                  type="number"
                  outlined
                  dense
                  hide-details="auto"
                  :rules="[rules.required, rules.minAge]"
                  suffix="years"
                  @input="onChange"
                />
                <div class="text-caption text--secondary mt-2">
                  Users must confirm they are at least this age to submit the form.
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </template>

        <template #content-styles>
          <v-expansion-panels v-model="expanded_styles" multiple class="section-group mb-6">
            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-view-dashboard"
                title="Container & Layout"
                subtitle="Configure form layout, container styling, and background effects"
              />
              <v-expansion-panel-content class="py-4">
                <div class="px-md-4">
                  <m-edit-select-enum
                    v-model="form.layout_mode"
                    enum_name="layout_style"
                    label="Layout Mode"
                    dialog_title="Select Layout Style"
                    @change="onChange"
                    class="mb-1"
                  />
                  <m-edit-css
                    label="Page Background"
                    v-model="form.page_background.style"
                    :default_value="form.page_background.default_style"
                    :mobile_value.sync="form.page_background.mobile_styles"
                    :default_mobile_value="form.page_background.default_mobile_styles"
                    class="mb-4"
                    @input="onChange"
                    @mobileValueUpdated="onChange"
                  />
                  <m-edit-css
                    label="Background Blur"
                    v-model="form.background_blur.style"
                    :default_value="form.background_blur.default_style"
                    :mobile_value.sync="form.background_blur.mobile_styles"
                    :default_mobile_value="form.background_blur.default_mobile_styles"
                    class="mb-4"
                    @input="onChange"
                    @mobileValueUpdated="onChange"
                  />
                  <m-edit-css
                    label="Container"
                    v-model="form.container.style"
                    :default_value="form.container.default_style"
                    :mobile_value.sync="form.container.mobile_styles"
                    :default_mobile_value="form.container.default_mobile_styles"
                    class="mb-4"
                    @input="onChange"
                    @mobileValueUpdated="onChange"
                  />
                  <m-edit-css
                    label="Container Content"
                    v-model="form.container_content.style"
                    :default_value="form.container_content.default_style"
                    :mobile_value.sync="form.container_content.mobile_styles"
                    :default_mobile_value="form.container_content.default_mobile_styles"
                    class="mb-4"
                    @input="onChange"
                    @mobileValueUpdated="onChange"
                  />
                  <m-edit-css
                    label="Background Overlay"
                    v-model="form.container_background.style"
                    :default_value="form.container_background.default_style"
                    :mobile_value.sync="form.container_background.mobile_styles"
                    :default_mobile_value="form.container_background.default_mobile_styles"
                    @input="onChange"
                    @mobileValueUpdated="onChange"
                  />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-format-header-1"
                title="Header & Content"
                subtitle="Customize header text, logo display, and content lines"
              />
              <v-expansion-panel-content class="pt-2 pb-4">
                <div class="px-md-4">
                  <v-switch
                    v-model="form.use_logo"
                    label="Show Logo"
                    color="primary"
                    class="mb-2"
                    @change="onChange"
                  />
                  <m-edit-css
                    label="Header Background"
                    v-model="form.header_background.style"
                    :default_value="form.header_background.default_style"
                    :mobile_value.sync="form.header_background.mobile_styles"
                    :default_mobile_value="form.header_background.default_mobile_styles"
                    class="mb-8"
                    @input="onChange"
                    @mobileValueUpdated="onChange"
                  />
                  <m-edit-styled-text
                    label="Banner Text"
                    v-model="form.banner_text"
                    hint="Main header text for the form"
                    class="mb-4"
                    @input="onChange"
                  />
                  <m-edit-styled-text
                    label="Line 1"
                    v-model="form.line_1"
                    hint="First custom text line"
                    class="mb-4"
                    @input="onChange"
                  />
                  <m-edit-styled-text
                    label="Line 2"
                    v-model="form.line_2"
                    hint="Second custom text line"
                    class="mb-4"
                    @input="onChange"
                  />
                  <m-edit-styled-text
                    label="Line 3"
                    v-model="form.line_3"
                    hint="Third custom text line"
                    class="mb-4"
                    @input="onChange"
                  />
                  <m-edit-styled-text
                    label="Personal Information Heading"
                    v-model="form.personal_info_heading"
                    hint="Heading for the personal information section"
                    class="mb-4"
                    @input="onChange"
                  />
                  <m-edit-styled-text
                    label="Contact Information Heading"
                    v-model="form.contact_info_heading"
                    hint="Heading for the contact information section"
                    @input="onChange"
                  />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-image-multiple"
                title="Images & Media"
                subtitle="Manage logo, side image, and background images"
              />
              <v-expansion-panel-content class="py-4">
                <div class="px-md-4">
                  <div class="mb-4">
                    <div class="sr-only">Logo Image</div>
                    <m-image-selector
                      v-if="form.use_logo"
                      v-model="form.logo_image.url"
                      :organization_id="$route.params.organization_id"
                      label="Logo Image"
                      @input="onChange"
                      class="mb-2"
                      ref="logoImageInput"
                      data-validation-target="logoImageInput"
                    />
                    <m-edit-css
                      v-if="form.use_logo"
                      label="Logo Image"
                      v-model="form.logo_image.style"
                      :default_value="form.logo_image.default_style"
                      :mobile_value.sync="form.logo_image.mobile_styles"
                      :default_mobile_value="form.logo_image.default_mobile_styles"
                      @input="onChange"
                      @mobileValueUpdated="onChange"
                    />
                    <v-alert v-if="!form.use_logo" type="info" dense text class="mt-2">
                      Enable "Show Logo" in the Header & Content section to configure logo image
                      settings.
                    </v-alert>
                  </div>
                  <div class="mb-6">
                    <div class="sr-only">Side Image</div>
                    <m-image-selector
                      v-model="form.side_image.url"
                      :organization_id="$route.params.organization_id"
                      label="Side Image"
                      hint="Recommended: JPG with aspect ratio of 7:12 (e.g. 700px width x 1200px height)"
                      class="image-selector mb-2"
                      @input="onChange"
                    />
                    <m-edit-css
                      label="Side Image"
                      v-model="form.side_image.style"
                      :default_value="form.side_image.default_style"
                      :mobile_value.sync="form.side_image.mobile_styles"
                      :default_mobile_value="form.side_image.default_mobile_styles"
                      @input="onChange"
                      @mobileValueUpdated="onChange"
                    />
                  </div>
                  <div>
                    <div class="sr-only">Page Background Image</div>
                    <m-image-selector
                      v-model="form.page_background.url"
                      :organization_id="$route.params.organization_id"
                      label="Background Image"
                      class="image-selector"
                      hint="Recommended: JPG with aspect ratio of 4:3 (e.g. 1600px width x 1200px height)"
                      @input="onChange"
                    />
                  </div>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-form-textbox"
                title="Form Fields"
                subtitle="Control the visibility, requirement, and styling of each input field"
              />
              <v-expansion-panel-content>
                <div class="mt-4">
                  <v-row>
                    <v-col cols="12" md="6">
                      <h4 class="mb-3">First Name</h4>
                      <m-edit-styled-text
                        label="First Name Field"
                        v-model="form.first_name"
                        hint="First name input field settings"
                        class="mb-4"
                        @input="onChange"
                      />
                      <v-select
                        :items="$rc.enumChoices('styled_field_mode', false)"
                        v-model="form.first_name.field_mode"
                        label="Field requirement"
                        dense
                        outlined
                        class="mb-3"
                        @change="onChange"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <h4 class="mb-3">Last Name</h4>
                      <m-edit-styled-text
                        label="Last Name Field"
                        v-model="form.last_name"
                        hint="Last name input field settings"
                        class="mb-4"
                        @input="onChange"
                      />
                      <v-select
                        :items="$rc.enumChoices('styled_field_mode', false)"
                        v-model="form.last_name.field_mode"
                        label="Field requirement"
                        dense
                        outlined
                        class="mb-3"
                        @change="onChange"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="6">
                      <h4 class="mb-3">Birthday</h4>
                      <m-edit-styled-text
                        label="Birthday Field"
                        v-model="form.birthday"
                        hint="Birthday input field settings"
                        class="mb-4"
                      />
                      <v-select
                        :items="$rc.enumChoices('styled_field_mode', false)"
                        v-model="form.birthday.field_mode"
                        label="Field requirement"
                        dense
                        outlined
                        class="mb-3"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <h4 class="mb-3">Phone Number</h4>
                      <m-edit-styled-text
                        label="Phone Number Field"
                        v-model="form.phone_number"
                        hint="Phone number input field settings"
                        class="mb-4"
                      />
                      <p>
                        <em>
                          Phone number field requirement is managed in <rc-link
                          text="General Settings"
                          text_transform="none"
                          :open_in_new_tab="false"
                          inline
                          @click="goToAgreementSettings('phoneFieldRequirement')"
                      />
                        </em>
                      </p>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="6">
                      <h4 class="mb-3">Email Address</h4>
                      <m-edit-styled-text
                        label="Email Address Field"
                        v-model="form.email_address"
                        hint="Email address input field settings"
                        class="mb-4"
                      />
                      <p>
                        <em>
                          Email address field requirement is managed in <rc-link
                          text="General Settings"
                          text_transform="none"
                          :open_in_new_tab="false"
                          inline
                          @click="goToAgreementSettings('emailFieldRequirement')"
                      />
                        </em>
                      </p>
                    </v-col>
                  </v-row>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-button-cursor"
                title="Submit Button"
                subtitle="Customize the appearance and text of the form submission button"
              />
              <v-expansion-panel-content>
                <div class="mt-4">
                  <m-edit-css
                    label="Submit Button"
                    v-model="form.send_button.style"
                    :default_value="form.send_button.default_style"
                    :mobile_value.sync="form.send_button.mobile_styles"
                    :default_mobile_value="form.send_button.default_mobile_styles"
                    class="mb-4"
                  />
                  <m-edit-styled-text
                    label="Submit Button Text"
                    v-model="form.send_button_text"
                    hint="Text displayed on the submit button"
                  />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <expansion-panel-header-rich
                icon="mdi-check-circle"
                title="Thank You Page"
                subtitle="Configure the content displayed after a successful submission"
              />
              <v-expansion-panel-content>
                <div class="mt-4">
                  <m-edit-styled-text
                    label="Thank You Line 1"
                    v-model="form.thank_you_line_1"
                    hint="First line of thank you message"
                    class="mb-4"
                  />
                  <m-edit-styled-text
                    label="Thank You Line 2"
                    v-model="form.thank_you_line_2"
                    hint="Second line of thank you message"
                    class="mb-4"
                  />
                  <m-edit-styled-text
                    label="Thank You Button Text"
                    v-model="form.thank_you_button_text"
                    hint="Text displayed on the thank you button"
                    class="mb-4"
                  />
                  <m-edit-css
                    label="Thank You Button"
                    v-model="form.thank_you_button.style"
                    :default_value="form.thank_you_button.default_style"
                    :mobile_value.sync="form.thank_you_button.mobile_styles"
                    :default_mobile_value="form.thank_you_button.default_mobile_styles"
                    class="mb-4"
                  />
                  <m-edit-css
                    label="Loading Spinner"
                    v-model="form.spinner.style"
                    :default_value="form.spinner.default_style"
                    :mobile_value.sync="form.spinner.mobile_styles"
                    :default_mobile_value="form.spinner.default_mobile_styles"
                    hint="Styling for the loading spinner that appears after form submission"
                    class="mb-4"
                    @input="onChange"
                    @mobileValueUpdated="onChange"
                  />
                  <m-edit-css
                    label="Success Icon"
                    v-model="form.check_in_success_icon.style"
                    :default_value="form.check_in_success_icon.default_style"
                    :mobile_value.sync="form.check_in_success_icon.mobile_styles"
                    :default_mobile_value="form.check_in_success_icon.default_mobile_styles"
                    hint="Styling for the loading spinner that appears after form submission"
                    class="mb-4"
                    @input="onChange"
                    @mobileValueUpdated="onChange"
                  />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </template>

        <template #preview>
          <widget-preview-panel
            ref="preview-panel"
            :preview_mode="preview_mode"
            :is_fullscreen="left_pane_collapsed"
            :is_loading="is_preview_loading"
            :dimensions="preview_dimensions"
            :initial_size_mode="use_actual_size"
            @fullscreen-toggle="left_pane_collapsed = !left_pane_collapsed"
            @size-mode-change="use_actual_size = $event"
          >
            <template #floating-controls>
              <widget-editor-floating-preview-control
                :visible="floating_control_visible"
                :preview_mode="preview_mode"
                :current_page="current_page"
                :pages="guest_check_in_pages"
                :supports_test_data="true"
                :view_modes="['tablet-landscape', 'tablet', 'mobile']"
                @preview-mode-change="onPreviewModeChange"
                @page-change="onPageChange"
                @auto-fill-test-data="onAutoFillTestData"
                @dismiss="onDismissFloatingControl"
              />
            </template>

            <template #preview>
              <iframe
                v-if="form && form.guest_check_in_form_id"
                :key="preview_nonce"
                :src="previewSrc"
                class="preview-iframe"
                frameborder="0"
                @load="onPreviewLoad"
                ref="preview-iframe"
              />
              <div v-else class="preview-placeholder">
                <v-icon size="48" color="grey lighten-1">mdi-form-select</v-icon>
                <div class="text-caption text--secondary mt-2">
                  Save form to see preview
                </div>
              </div>
            </template>
          </widget-preview-panel>
        </template>
      </widget-editor-layout>

      <v-row v-else>
        <v-col cols="12" class="text-center">
          <v-progress-circular indeterminate color="primary" size="64" />
          <div class="mt-3">Loading form...</div>
        </v-col>
      </v-row>
    </v-container>
  </m-pageframe>
</template>

<script>
import MEditStyledText from '@/components/input/m-edit-styled-text.vue';
import MEditCss from '@/components/input/m-edit-css.vue';
import MEditSelectOrganizationOwnedEndpoint from '@/components/input/m-edit-select-organization-owned-endpoint.vue';
import EditMessagePatternsCompact from '@/components/input/edit-message-patterns-compact.vue';
import MImageSelector from '@/components/input/image-selector.vue';
import ExpansionPanelHeaderRich from '@/components/shared/expansion-panels/expansion-panel-header-rich.vue';
import WidgetEditorLayout from '@/components/widget/widget-editor-layout.vue';
import WidgetPreviewPanel from '@/components/widget/widget-preview-panel.vue';
import WidgetEditorFloatingPreviewControl from '@/components/widget/widget-editor-floating-preview-control.vue';
import {
  composeGuestCheckInPreviewParams,
  sendSettingsToWidget,
  sendPageChangeToWidget,
} from '@/js/widget/guest-check-in-preview-composer.js';
import { buildWidgetUrl } from '@/js/widget/widget-url-builder.js';
import GuidedValidationMixin from '@/mixins/guided-validation.js';

export default {
  name: 'GuestCheckInEdit',

  mixins: [GuidedValidationMixin],

  components: {
    MEditStyledText,
    MEditCss,
    MEditSelectOrganizationOwnedEndpoint,
    EditMessagePatternsCompact,
    MImageSelector,
    ExpansionPanelHeaderRich,
    WidgetEditorLayout,
    WidgetPreviewPanel,
    WidgetEditorFloatingPreviewControl,
  },

  mounted() {
    this.loadFormAndEditStatus();
    this.loadAvailableLists();
    this.loadIntegrationFeatures();
    this.loadDefaultLegalPatterns();
  },

  data() {
    return {
      form: null,
      saving: false,
      save_debounce_handle: null,
      suppress_autosave: true,
      preview_nonce: 0,
      is_preview_loading: false,
      active_tab: 0,
      expanded_general: [0, 1],
      expanded_styles: [0],
      available_lists: [],
      customer_auto_enrich_feature_available: false,
      tag_new_form_customers_available: false,
      integration_tags: [],
      floating_control_visible: true,
      preview_mode: 'tablet-landscape', // 'tablet-landscape', 'tablet', 'mobile'
      current_page: 'form',
      left_pane_collapsed: false,
      use_actual_size: 0,
      default_legal_patterns: {
        double_opt_in_pattern: '',
        after_double_opt_in_message: '',
        email_agreement_implied: '',
        email_agreement_checkbox: '',
        phone_agreement_implied: '',
        phone_agreement_checkbox: '',
      },

      rules: {
        required: value => !!value || 'This field is required',
        minAge: value => (value >= 1 && value <= 100) || 'Age must be between 1 and 100',
      },

      validation_warnings: [],
      endpoint_validation_error: null,
      edit_status: null,

      guest_check_in_pages: [
        { value: 'form', label: 'Form Page', icon: 'mdi-form-select' },
        { value: 'thanks', label: 'Thank You Page', icon: 'mdi-heart' },
      ],
    };
  },

  computed: {
    back_route() {
      return {
        name: 'search-forms-guest-check-in',
        params: {
          organization_id: this.$route.params.organization_id,
        },
      };
    },

    public_url() {
      if (!this.edit_status?.has_published_version) {
        return '';
      }
      return `${window.location.origin}/app/guest-check-in/${this.form.guest_check_in_form_id}`;
    },

    publicUrlHint() {
      if (this.is_published) {
        return 'Share this URL with guests to access the check-in form';
      } else {
        return 'Publish the form to get the shareable URL';
      }
    },

    previewURL() {
      if (!this.form || !this.form.guest_check_in_form_id) {
        return null;
      }
      return `${window.location.origin}/app/guest-check-in/${this.form.guest_check_in_form_id}`;
    },

    previewSrc() {
      if (!this.form || !this.form.guest_check_in_form_id) {
        return null;
      }

      const params = {};
      if (this.preview_mode === 'mobile') {
        params.mobile = 'true';
      }

      return buildWidgetUrl('guest-check-in', params);
    },

    preview_dimensions() {
      const dimensions = {
        mobile: { width: 410, height: 844 },
        tablet: { width: 1024, height: 1366 },
        'tablet-landscape': { width: 1366, height: 1024 },
      };
      return dimensions[this.preview_mode] || dimensions['tablet-landscape'];
    },

    preview_wrapper_style() {
      const dims = this.preview_dimensions;
      const scale = this.use_actual_size === 1 ? 1 : this.preview_scale;
      return {
        transform: `scale(${scale})`,
        transformOrigin: 'top center',
        width: `${dims.width}px`,
        height: `${dims.height}px`,
      };
    },

    filteredConsentGatheringOptions() {
      return ['consent-via-field-submission', 'checkbox-consent'];
    },

    email_field_mode_options() {
      const all_options = this.$rc.enumChoices('styled_field_mode', false);
      return all_options;
    },

    phone_field_mode_options() {
      const all_options = this.$rc.enumChoices('styled_field_mode', false);
      return all_options;
    },

    integration_tags_with_none() {
      const no_tag_option = {
        tag_id: null,
        tag_label: 'No Tag',
      };
      return [no_tag_option, ...this.integration_tags];
    },

    show_tag_selection() {
      if (!this.tag_new_form_customers_available) {
        return false;
      }

      const creates_customers_on_submit =
        this.form &&
        (this.form.on_submit_crm_customer_upsert_mode === 'create-only' ||
          this.form.on_submit_crm_customer_upsert_mode === 'create-or-update');

      const creates_customers_after_opt_in =
        this.form &&
        (this.form.after_double_opt_in_crm_customer_upsert_mode === 'create-only' ||
          this.form.after_double_opt_in_crm_customer_upsert_mode === 'create-or-update');

      return creates_customers_on_submit || creates_customers_after_opt_in;
    },

    is_published() {
      return this.edit_status?.has_published_version;
    },

    has_draft_changes() {
      if (!this.edit_status) return false;
      return this.edit_status.has_unpublished_changes;
    },

    can_unarchive() {
      if (this.$rc.$state.isAuthActorSystemAdmin()) {
        return this.$rc.$state.isDemoOrTestOrganization();
      }
      return this.$rc.$state.isOrganizationAdmin() && !this.$rc.$state.isAuthActorSystemAdmin();
    },
  },

  methods: {
    handleEndpointValidation(payload) {
      if (!payload.isValid) {
        this.endpoint_validation_error = payload.message;
      } else {
        this.endpoint_validation_error = null;
      }
      this.validateForm();
    },

    fixWarning(warning) {
      this.mixinGuideToFix(warning, {
        active_tab_model: 'active_tab',
        panel_model: warning.location.tab === 0 ? 'expanded_general' : 'expanded_styles',
      });
    },

    goToAgreementSettings(validation_target = 'emailAgreementSettings') {
      this.active_tab = 0;
      this.expanded_general = [3];
      setTimeout(() => {
        this.scrollToAndHighlightElement(validation_target);
      }, 100)
    },

    onPreviewLoad() {
      this.is_preview_loading = false;

      this.$nextTick(() => {
        this.sendFormDataToWidget();
      });
    },

    sendFormDataToWidget() {
      if (!this.$refs['preview-iframe'] || !this.form) {
        return;
      }

      const settings = composeGuestCheckInPreviewParams({
        form: this.form,
        is_test_submission: true,
        preview_mode: this.preview_mode,
        is_template_preview: false,
      });

      sendSettingsToWidget(this.$refs['preview-iframe'], settings);

      if (this.current_page === 'thanks') {
        sendPageChangeToWidget(this.$refs['preview-iframe'], 'thanks');
      }
    },

    getDefaultEmailAgreementPattern() {
      if (this.form.email_consent_gathering_state === 'consent-via-field-submission') {
        return this.default_legal_patterns.email_agreement_implied;
      } else if (this.form.email_consent_gathering_state === 'checkbox-consent') {
        return this.default_legal_patterns.email_agreement_checkbox;
      }
      return this.default_legal_patterns.email_agreement_implied;
    },

    getDefaultPhoneAgreementPattern() {
      if (this.form.phone_number_consent_gathering_state === 'consent-via-field-submission') {
        return this.default_legal_patterns.phone_agreement_implied;
      } else if (this.form.phone_number_consent_gathering_state === 'checkbox-consent') {
        return this.default_legal_patterns.phone_agreement_checkbox;
      }
      return this.default_legal_patterns.phone_agreement_implied;
    },

    resetEmailAgreementToDefault() {
      const default_pattern = this.getDefaultEmailAgreementPattern();
      if (default_pattern) {
        this.form.email_consent_agreement_custom_patterns = [default_pattern];
        this.onChange();
      }
    },

    resetPhoneAgreementToDefault() {
      const default_pattern = this.getDefaultPhoneAgreementPattern();
      if (default_pattern) {
        this.form.phone_number_consent_agreement_custom_patterns = [default_pattern];
        this.onChange();
      }
    },

    onPageChange(page) {
      this.current_page = page;

      this.$nextTick(() => {
        if (this.$refs['preview-iframe']) {
          sendPageChangeToWidget(this.$refs['preview-iframe'], page);
        }
      });
    },

    onPreviewModeChange(mode) {
      this.preview_mode = mode;
      this.current_page = 'form';
    },

    onAutoFillTestData() {
      this.$nextTick(() => {
        if (this.$refs['preview-iframe']) {
          this.$refs['preview-iframe'].contentWindow.postMessage(
            [
              'auto-fill-test-data',
              {
                first_name: 'John',
                last_name: 'Doe',
                email_address: 'john.doe@example.com',
                phone_number: '+1 (555) 123-4567',
                birthday: '01/15/1990',
              },
            ],
            '*'
          );
        }
      });
    },

    onDismissFloatingControl() {
      this.floating_control_visible = false;
    },

    onChange() {
      if (this.suppress_autosave) {
        return;
      }

      this.validateForm();

      if (this.save_debounce_handle) {
        clearTimeout(this.save_debounce_handle);
      }
      this.save_debounce_handle = setTimeout(() => {
        if (this.form) {
          this.saveForm();
        }
        this.preview_nonce = Date.now();

        // Update widget preview with new form data
        this.$nextTick(() => {
          this.sendFormDataToWidget();
        });
      }, 750);
    },

    onEmailFieldModeChange() {
      this.autoAdjustEmailConsentState();
      this.onChange();
    },

    onPhoneFieldModeChange() {
      this.autoAdjustPhoneConsentState();
      this.onChange();
    },

    onEmailConsentGatheringChange() {
      this.onChange();
    },

    onPhoneConsentGatheringChange() {
      this.onChange();
    },

    autoAdjustEmailConsentState() {
      if (!this.form || !this.form.email_address) return;

      const isHidden = this.form.email_address.field_mode === 'hidden';

      if (isHidden) {
        this.form.email_consent_gathering_state = 'none';
        this.$rc.snackbar(
          'Email consent automatically set to "No Consent Required" because the email field is hidden.'
        );
      } else if (this.form.email_consent_gathering_state === 'none') {
        this.form.email_consent_gathering_state = 'consent-via-field-submission';
        this.$rc.snackbar(
          'Email consent automatically set to "Implied Consent" because the email field is visible.'
        );
      }
    },

    autoAdjustPhoneConsentState() {
      if (!this.form || !this.form.phone_number) return;

      const isHidden = this.form.phone_number.field_mode === 'hidden';

      if (isHidden) {
        this.form.phone_number_consent_gathering_state = 'none';
        this.$rc.snackbar(
          'Phone consent automatically set to "No Consent Required" because the phone field is hidden.'
        );
      } else if (this.form.phone_number_consent_gathering_state === 'none') {
        this.form.phone_number_consent_gathering_state = 'consent-via-field-submission';
        this.$rc.snackbar(
          'Phone consent automatically set to "Implied Consent" because the phone field is visible.'
        );
      }
    },
    loadAvailableLists() {
      this.$rc.rest({
        spinner: false,
        endpoint: 'LIST: /api/v1/autocomplete/lists',

        params: {},

        on_success: data => {
          let items = [];

          for (let i = 0; i < data.results.length; i++) {
            items.push({
              text: data.results[i].text,
              value: data.results[i].value,
            });
          }

          this.available_lists = items;
        },

        on_error: () => {
          this.$rc.snackbar(
            'Unable to load available lists, please try refreshing the page.',
            1500
          );
        },
      });
    },

    loadFormAndEditStatus() {
      this.suppress_autosave = true;
      this.$rc.rest({
        spinner: true,
        endpoint: 'GET: /api/v1/guest-check-in/form/:guest_check_in_form_id/edit-status',
        params: {
          organization_id: this.$route.params.organization_id,
          guest_check_in_form_id: this.$route.params.guest_check_in_form_id,
        },
        on_success: data => {
          this.edit_status = { ...data };
          this.form = { ...data.draft_form };
          this.initializeStyledElements();
          this.validateForm();
          this.$nextTick(() => {
            this.suppress_autosave = false;
          });
        },
        on_error: () => {
          this.$rc.snackbar('Unable to load form, please try refreshing the page.', 1500);
          this.suppress_autosave = false;
        },
      });
    },

    initializeStyledElements() {
      if (!this.form) return;

      const initializeStyledElement = element => {
        if (!element) {
          return { text: '', style: '' };
        }
        return {
          text: element.text || '',
          style: element.style || '',
          default_text: element.default_text || '',
          default_style: element.default_style || '',
          ...element,
        };
      };

      this.form.banner_text = initializeStyledElement(this.form.banner_text);
      this.form.line_1 = initializeStyledElement(this.form.line_1);
      this.form.line_2 = initializeStyledElement(this.form.line_2);
      this.form.line_3 = initializeStyledElement(this.form.line_3);
      this.form.thank_you_line_1 = initializeStyledElement(this.form.thank_you_line_1);
      this.form.thank_you_line_2 = initializeStyledElement(this.form.thank_you_line_2);
      this.form.send_button_text = initializeStyledElement(this.form.send_button_text);
      this.form.spinner = initializeStyledElement(this.form.spinner);
      this.form.email_agreement = initializeStyledElement(this.form.email_agreement);
      this.form.phone_number_agreement = initializeStyledElement(this.form.phone_number_agreement);
    },

    saveForm() {
      if (this.saving) return;
      this.saving = true;
      this.is_preview_loading = true;

      this.$rc.rest({
        spinner: false,
        endpoint: 'PUT: /api/v1/guest-check-in/form/settings',

        params: this.form,

        on_success: data => {
          if (this.save_debounce_handle) {
            clearTimeout(this.save_debounce_handle);
            this.save_debounce_handle = null;
          }

          this.suppress_autosave = true;
          if (this.form && data) {
            Object.keys(data).forEach(k => {
              this.$set(this.form, k, data[k]);
            });
          }

          this.$nextTick(() => {
            this.suppress_autosave = false;
            this.preview_nonce = (this.preview_nonce || 0) + 1;
            this.saving = false;
            this.loadFormAndEditStatus();
          });
        },

        on_error: () => {
          this.$rc.snackbar('Unable to save form, please try again.', 1500);
          this.saving = false;
          this.is_preview_loading = false;
          if (this.save_debounce_handle) {
            clearTimeout(this.save_debounce_handle);
            this.save_debounce_handle = null;
          }
        },
      });
    },

    get_default_double_opt_in_message_patterns() {
      return this.default_legal_patterns.double_opt_in_pattern
        ? [this.default_legal_patterns.double_opt_in_pattern]
        : ['Reply YES to confirm your subscription.'];
    },
    get_default_after_double_opt_in_message_patterns() {
      return this.default_legal_patterns.after_double_opt_in_message
        ? [this.default_legal_patterns.after_double_opt_in_message]
        : ["You're confirmed! Thanks for joining."];
    },
    get_default_no_double_opt_in_after_submission_message_patterns() {
      return this.default_legal_patterns.no_double_opt_in_after_submission_message
        ? [this.default_legal_patterns.no_double_opt_in_after_submission_message]
        : ["Thank you for checking in! You're now subscribed to our updates."];
    },

    loadDefaultLegalPatterns() {
      this.$rc.rest(
        'GET: /api/v1/guest-check-in/default-legal-patterns',
        {},
        data => {
          this.default_legal_patterns = {
            double_opt_in_pattern: data.double_opt_in_pattern || '',
            after_double_opt_in_message: data.after_double_opt_in_message || '',
            email_agreement_implied: data.email_agreement_implied || '',
            email_agreement_checkbox: data.email_agreement_checkbox || '',
            phone_agreement_implied: data.phone_agreement_implied || '',
            phone_agreement_checkbox: data.phone_agreement_checkbox || '',
          };
        },
        () => {}
      );
    },

    copyPublicUrl() {
      if (!this.form || !this.form.guest_check_in_form_id) {
        this.$rc.alert('Form must be saved before copying URL');
        return;
      }

      const public_url = `${window.location.origin}/app/guest-check-in/${this.form.guest_check_in_form_id}`;
      this.$rc.copy(public_url);
    },

    generateQRCode(qrCodeDialog) {
      if (!this.form || !this.form.guest_check_in_form_id) {
        this.$rc.alert('Form must be saved before generating QR code');
        return;
      }

      this.$rc.rest({
        spinner: true,
        endpoint: 'PUT: /api/v1/guest-check-in/forms/generate-qr-code',
        params: {
          guest_check_in_form_id: this.form.guest_check_in_form_id,
        },
        on_success: data => {
          qrCodeDialog.showDialog(data.qr_code_id);
        },
        on_error: 'Unable to generate QR code',
      });
    },

    loadIntegrationFeatures() {
      this.checkFeatureAvailability('customers-auto-enrich-from-redchirp', is_supported => {
        this.customer_auto_enrich_feature_available = is_supported;
      });

      this.checkFeatureAvailability('tag-new-lightbox-customers-from-redchirp', is_supported => {
        this.tag_new_form_customers_available = is_supported;

        if (is_supported) {
          this.loadIntegrationTags('COMMERCE7');
        } else {
          this.integration_tags = [];
        }
      });
    },

    checkFeatureAvailability(feature_identifier, on_success) {
      this.$rc.hasEnabledIntegrationFeature(feature_identifier, on_success, () => {
        on_success(false);
      });
    },

    loadIntegrationTags(integration_type) {
      this.$rc.rest({
        spinner: false,
        endpoint:
          'GET: /api/v1/integrations/crm/tags/' +
          this.$route.params.organization_id +
          '/' +
          integration_type,

        on_success: data => {
          this.integration_tags = data.tags || [];
        },

        on_error: () => {
          this.integration_tags = [];
        },
      });
    },

    onNewCustomerTagChange(tag_id) {
      if (tag_id && tag_id !== null) {
        this.form.new_customer_tag_integration_type = 'COMMERCE7';
      } else {
        this.form.new_customer_tag_integration_type = null;
      }
      this.onChange();
    },

    validateForm() {
      this.validation_warnings = [];
      if (!this.form) return;

      if (!this.form.internal_label || this.form.internal_label.trim() === '') {
        this.validation_warnings.push(
          this.mixinCreateWarning('A Form Name is required.', {
            tab: 0,
            panel: 0,
            target: 'formNameInput',
          })
        );
      }

      if (this.form.age_gate_min_age_in_years <= 0) {
        this.validation_warnings.push(
          this.mixinCreateWarning('Minimum Age must be greater than zero.', {
            tab: 0,
            panel: 4,
            target: 'ageGateInput',
          })
        );
      }

      if (this.form.age_gate_min_age_in_years > 100) {
        this.validation_warnings.push(
          this.mixinCreateWarning('Minimum Age must be 100 or less.', {
            tab: 0,
            panel: 4,
            target: 'ageGateInput',
          })
        );
      }

      if (!this.form.double_opt_in_state) {
        this.validation_warnings.push(
          this.mixinCreateWarning('The Double Opt-In state must be set.', {
            tab: 0,
            panel: 1,
            target: 'doiStateSelect',
          })
        );
      }

      const doiEnabled = this.form.double_opt_in_state === 'enabled';
      const afterSubmitMsgEnabled =
        this.form.double_opt_in_state === 'disabled' &&
        this.form.no_double_opt_in_after_submission_message_enabled;
      if ((doiEnabled || afterSubmitMsgEnabled) && !this.form.send_from_endpoint) {
        this.validation_warnings.push(
          this.mixinCreateWarning('A "Send messages from" number is required for messaging.', {
            tab: 0,
            panel: 1,
            target: 'sendFromEndpointSelect',
          })
        );
      }

      if (
        this.form.email_consent_gathering_state !== 'none' &&
        this.form.email_address.field_mode === 'hidden'
      ) {
        this.validation_warnings.push(
          this.mixinCreateWarning('Email consent is enabled, but the Email field is hidden.', {
            tab: 0,
            panel: 3,
            target: 'emailAgreementSettings',
          })
        );
      }
      if (
        this.form.phone_number_consent_gathering_state !== 'none' &&
        this.form.phone_number.field_mode === 'hidden'
      ) {
        this.validation_warnings.push(
          this.mixinCreateWarning('Phone consent is enabled, but the Phone field is hidden.', {
            tab: 0,
            panel: 3,
            target: 'phoneAgreementSettings',
          })
        );
      }

      if (
        this.form.email_consent_gathering_state === 'consent-via-field-submission' &&
        this.form.email_address.field_mode === 'required'
      ) {
        this.validation_warnings.push(
          this.mixinCreateWarning(
            'Using the email agreement with no checkbox requires the email field to be optional.',
            { tab: 0, panel: 3, target: 'emailAgreementSettings' }
          )
        );
      }
      if (
        this.form.phone_number_consent_gathering_state === 'consent-via-field-submission' &&
        this.form.phone_number.field_mode === 'required'
      ) {
        this.validation_warnings.push(
          this.mixinCreateWarning(
            'Using the phone agreement with no checkbox requires the phone field to be optional.',
            { tab: 0, panel: 3, target: 'phoneAgreementSettings' }
          )
        );
      }
      if (this.endpoint_validation_error) {
        this.validation_warnings.push(
          this.mixinCreateWarning(this.endpoint_validation_error, {
            tab: 0,
            panel: 1,
            target: 'sendFromEndpointSelect',
          })
        );
      }
      if (this.form.use_logo && !this.form.logo_image.url) {
        this.validation_warnings.push(
          this.mixinCreateWarning('Logo is enabled, but no logo image is set.', {
            tab: 1,
            panel: 2,
            target: 'logoImageInput',
          })
        );
      }

      if (
        this.form.double_opt_in_state === 'enabled' &&
        this.form.double_opt_in_message_patterns.length
      ) {
        //Check if all patterns are empty
        if (
          this.form.double_opt_in_message_patterns.every(
            pattern => !pattern || pattern.trim().length === 0
          )
        ) {
          this.validation_warnings.push(
            this.mixinCreateWarning('Double opt-in message is required.', {
              tab: 0,
              panel: 1,
              target: 'doiMessageInput',
            })
          );
        }
      }
      if (
        this.form.double_opt_in_state === 'enabled' &&
        this.form.after_double_opt_in_message_patterns.length
      ) {
        //Check if all patterns are empty
        if (
          this.form.after_double_opt_in_message_patterns.every(
            pattern => !pattern || pattern.trim().length === 0
          )
        ) {
          this.validation_warnings.push(
            this.mixinCreateWarning('After double opt-in message is required.', {
              tab: 0,
              panel: 1,
              target: 'afterDOIInput',
            })
          );
        }
      }
      if (
        this.form.double_opt_in_state === 'disabled' &&
        this.form.no_double_opt_in_after_submission_message_enabled &&
        this.form.no_double_opt_in_after_submission_message_patterns.length
      ) {
        //Check if all patterns are empty
        if (
          this.form.no_double_opt_in_after_submission_message_patterns.every(
            pattern => !pattern || pattern.trim().length === 0
          )
        ) {
          this.validation_warnings.push(
            this.mixinCreateWarning('After submission message is required.', {
              tab: 0,
              panel: 1,
              target: 'afterSubmitInput',
            })
          );
        }
      }
    },

    handlePublish(callbacks) {
      if (!this.form || !this.form.guest_check_in_form_id) {
        callbacks.onError('Form must be saved before publishing');
        return;
      }

      this.$rc.rest({
        spinner: false,
        endpoint:
          'PUT: /api/v1/guest-check-in/forms/:guest_check_in_form_id/create-publication-signature',
        params: {
          guest_check_in_form_id: this.form.guest_check_in_form_id,
          agreement_specimen:  this.$refs['publish-agreement'].getSpecimen(),
        },
        on_success: signature_data => {
          this.$rc.rest({
            spinner: false,
            endpoint: 'PUT: /api/v1/guest-check-in/forms/:guest_check_in_form_id/publish',
            params: {
              guest_check_in_form_id: this.form.guest_check_in_form_id,
              publication_signature_id: signature_data.signature_id,
            },
            on_success: () => {
              this.loadFormAndEditStatus();
              callbacks.onSuccess();
              this.$rc.snackbar('Form published successfully!', 3000);
            },
            on_error: error => {
              callbacks.onError(error.message || 'Failed to publish form. Please try again.');
            },
          });
        },
        on_error: error => {
          callbacks.onError(error.message || 'Failed to create signature. Please try again.');
        },
      });
    },

    handleDiscardChanges(callbacks) {
      if (!this.form || !this.form.guest_check_in_form_id) {
        callbacks.onError();
        return;
      }

      this.$rc.rest({
        spinner: false,
        endpoint: 'PUT: /api/v1/guest-check-in/forms/:guest_check_in_form_id/discard-changes',
        params: {
          guest_check_in_form_id: this.form.guest_check_in_form_id,
        },
        on_success: draft_form => {
          this.form = draft_form;
          this.initializeStyledElements();
          this.onChange();

          callbacks.onSuccess();
          this.$rc.snackbar('Changes discarded successfully!', 3000);
        },
        on_error: () => {
          callbacks.onError();
          this.$rc.snackbar('Failed to discard changes. Please try again.', 3000);
        },
      });
    },

    archiveForm(callbacks) {
      if (!this.form) {
        callbacks.onError();
        return;
      }

      this.$rc.rest({
        spinner: false,
        endpoint: 'PATCH: /api/v1/guest-check-in/forms/:guest_check_in_form_id/archive',
        params: {
          guest_check_in_form_id: this.form.guest_check_in_form_id,
          archived: true,
        },
        on_success: () => {
          callbacks.onSuccess();
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        },
        on_error: error => {
          callbacks.onError();
          this.$rc.snackbar(
            error.message || 'Failed to archive form. Please try again.',
            3000,
            'error'
          );
        },
      });
    },

    unarchiveForm() {
      this.$rc.confirm({
        title: `Unarchive Form`,
        body: `Are you sure you want to unarchive the draft version of the form: "${
          this.form ? this.form.internal_label : 'Unknown'
        }"? <br/><br/>This will not unarchive any published version of the form.`,
        btn_text_ok: 'Unarchive',
        btn_text_cancel: 'Cancel',
        on_ok: () => this.doUnarchive(),
      });
    },

    doUnarchive() {
      if (!this.form) return;
      this.$rc.rest({
        spinner: true,
        endpoint: 'PATCH: /api/v1/guest-check-in/forms/:guest_check_in_form_id/archive',
        params: {
          guest_check_in_form_id: this.form.guest_check_in_form_id,
          archived: false,
        },
        on_success: () => {
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        },
        on_error: error => {
          this.$rc.snackbar(
            error.message || 'Failed to unarchive form. Please try again.',
            3000,
            'error'
          );
        },
      });
    },
  },

  beforeRouteLeave(to, from, next) {
    if (this.$refs.widgetLayout) {
      this.$refs.widgetLayout.confirmLeaveWithUnpublishedChanges(next);
    } else {
      next();
    }
  },

  watch: {
    form: {
      handler() {
        this.onChange();
      },
      deep: true,
    },
  },
};
</script>

<style scoped lang="scss">
.preview-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.preview-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  background-color: #f5f5f5;
}

code {
  background: rgba(0, 0, 0, 0.05);
  padding: 2px 4px;
  border-radius: 3px;
  font-size: 0.8rem;
}

.section-group {
  transition: all 0.3s ease;
}

.image-selector :deep(.v-input__slot) {
  margin-bottom: 0;
}
</style>
